/**
 * byo-v4-personalise.js — stage 2 (P5).
 *
 * Ported by name from `byo-stages-v3.jsx`: `WrapChooser` (225), `TouchRow`
 * (266), `PersonalTouches` (273), and the `Toggle` / `field` / `Err` chrome.
 * DESIGN-SPEC.md §4.
 *
 * Three things worth knowing before changing anything here:
 *
 * 1. **Errors appear on Continue only, never while typing** (§4.2). The stage
 *    takes `showErrors` from the caller; nothing in here sets it. The first
 *    error scrolls into view with a 72px offset.
 * 2. **The switch is the only control on a touch** (D70). On shows its
 *    fields, off hides them, and the title is only a title. It used to open
 *    and close the fields too, so a tap on it folded away a touch that had
 *    just been paid for, and it looked as though it had gone.
 * 3. **This step is the touches and nothing else** (D70). How the box is
 *    given, and the keepsake box with it, moved to Wrap It Up; the "Who's it
 *    for" name and the "From" line are gone; the song is off until it can
 *    offer a library to pick from, and its row returns with the setting.
 *
 * The photo has to reach the cart as a resolvable URL (GIFTSHIP-API.md §4), so
 * it is uploaded when it is chosen rather than held for the cart write. A
 * failure keeps the file, shows the customer a plain retry, and blocks
 * Continue rather than pretending the photo is safe.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, useState, useRef, useMemo } from './byo-v4-vendor.js';
const { Icon, Pill } = (window.BYOV4 = window.BYOV4 || {});
/** The date the save-the-date picker may not go below: today + leadDays. */
export function minDate(leadDays = 2) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + leadDays);
  return d.toISOString().slice(0, 10);
}

/** "Sat, 4 Oct" — the design's short form for a chosen date. */
export function prettyDate(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/* Above this a photo gets the "keep this page open" line. A modern phone's
   JPEG is 2–5MB, so this is the size at which mobile data starts to be felt. */
const BIG_PHOTO = 3 * 1024 * 1024;

/** The status line under the upload bar, for the phase `uploadPhoto` reports. D55. */
export function uploadLine({ phase, pct }) {
  return phase === 'finishing' ? 'Nearly there — saving your photo…' : `Uploading your photo… ${pct}%`;
}

/**
 * UploadStatus — what the drop zone shows while a photo uploads (D55): the
 * photo, its name, a real progressbar and the line under it.
 *
 * The line is `aria-hidden` because it changes on every percent and a live
 * region reading "Uploading your photo… 12%, 13%, 14%" is noise. Screen
 * readers get the progressbar's value instead, plus the phase lines from the
 * live region the row keeps mounted (see PersonalTouches).
 */
export function UploadStatus({ upload, say }) {
  const finishing = upload.phase === 'finishing';
  return html`
    <div class=${`byo-v4-upload${finishing ? ' is-finishing' : ''}`}>
      ${upload.preview && html`<img class="byo-v4-photo__thumb" src=${upload.preview} alt="" />`}
      <div class="byo-v4-upload__meta">
        <div class="byo-v4-photo__name">${upload.name}</div>
        <div
          class="byo-v4-upload__track"
          role="progressbar"
          aria-label="Photo upload"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow=${upload.pct}
        >
          <span class="byo-v4-upload__fill" style=${`width:${upload.pct}%`}></span>
        </div>
        <div class="byo-v4-upload__say" aria-hidden="true">${say}</div>
        ${upload.big &&
        html`<div class="byo-v4-upload__hint">Big photos can take up to a minute on mobile — keep this page open.</div>`}
      </div>
    </div>
  `;
}

/** Err — `role="alert"`, coral, 13/600 with an 8px dot. §4.2. */
export function Err({ children }) {
  if (!children) return null;
  return html`
    <div class="byo-v4-err" role="alert">
      <span class="byo-v4-err__dot" aria-hidden="true"></span>
      <span>${children}</span>
    </div>
  `;
}

/** Toggle — the 51×31 switch. A real `role="switch"` button, per CLAUDE.md. */
export function Toggle({ on, onChange, label }) {
  return html`
    <button
      type="button"
      class=${`byo-v4-toggle${on ? ' is-on' : ''}`}
      role="switch"
      aria-checked=${on}
      aria-label=${label}
      onClick=${() => onChange(!on)}
    >
      <span class="byo-v4-toggle__knob"></span>
    </button>
  `;
}

/**
 * TouchRow — byo-stages-v3.jsx:266. Icon circle, title (with a coral dot when
 * this row is in error), the subtitle, and the switch. D70: the switch alone
 * decides whether the touch is in the box, and so whether it is paid for;
 * while it is on, its fields are shown.
 */
export function TouchRow({ id, icon, title, sub, on, err, onToggle, children }) {
  return html`
    <div class=${`byo-v4-touch${on ? ' is-on' : ''}${err ? ' is-err' : ''}`} data-touch=${id}>
      <div class="byo-v4-touch__head">
        <span class=${`byo-v4-touch__icon${on ? ' is-on' : ''}`} aria-hidden="true">
          <${Icon} name=${icon} size=${18} />
        </span>

        <div class="byo-v4-touch__label">
          <span class="byo-v4-touch__title">
            ${err && html`<span class="byo-v4-touch__dot" aria-hidden="true"></span>`}${title}
          </span>
          <span class="byo-v4-touch__sub">${sub}</span>
        </div>

        <${Toggle} on=${on} onChange=${onToggle} label=${title} />
      </div>

      ${on && html`<div class="byo-v4-touch__body">${children}</div>`}
    </div>
  `;
}

/**
 * WrapChooser — byo-stages-v3.jsx:225. The three wraps come from Giftship's
 * image step, never from settings (`sections/byo-v4-builder.liquid:404`), so
 * this renders whatever that step returns and says so plainly when it returns
 * nothing rather than drawing empty tiles. Drawn on Wrap It Up (D70).
 */
export function WrapChooser({ b, wraps, showErrors }) {
  const chosen = b.box.wrap != null ? wraps.find((w) => w.name === b.box.wrap) : null;

  return html`
    <div class="byo-v4-wraps">
      <div class="byo-v4-wraps__say">
        ${chosen ? `Wrapped in ${chosen.name} · Free` : 'Pick your wrap'}
      </div>
      ${showErrors && !b.wrapOk && html`<${Err}>Pick a wrap for your keepsake box<//>`}

      ${wraps.length === 0
        ? html`<div class="byo-v4-wraps__none">
            The wraps come from Giftship and could not be loaded. Refresh the page to try again.
          </div>`
        : html`<div class="byo-v4-wraps__grid" role="radiogroup" aria-label="Keepsake box wrap">
            ${wraps.map(
              (w) => html`
                <button
                  key=${w.name}
                  type="button"
                  class=${`byo-v4-wrap${b.box.wrap === w.name ? ' is-on' : ''}`}
                  role="radio"
                  aria-checked=${b.box.wrap === w.name}
                  onClick=${() => b.setWrap(w.name)}
                >
                  <img class="byo-v4-wrap__img" src=${w.img} alt="" />
                  <span class="byo-v4-wrap__name">${w.name}</span>
                </button>
              `
            )}
          </div>`}
    </div>
  `;
}

/* ---- the save-the-date calendar (D70) ----------------------------------- *
 * Days are whole numbers — days since 1 Jan 1970, in UTC — so there is no
 * time of day and no daylight saving anywhere in the arithmetic.
 */

const DAY_MS = 864e5;
const dayNum = (y, m, d) => Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
const isoOf = (n) => new Date(n * DAY_MS).toISOString().slice(0, 10);
const weekdayOf = (n) => new Date(n * DAY_MS).getUTCDay();
const dayOfIso = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? dayNum(+m[1], +m[2], +m[3]) : null;
};
const monthKey = (v) => v.y * 12 + (v.m - 1);
const monthOfDay = (n) => {
  const d = new Date(n * DAY_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 };
};
const addMonths = (v, k) => {
  const t = monthKey(v) + k;
  return { y: Math.floor(t / 12), m: (t % 12) + 1 };
};

/**
 * The dispatch rules the theme's own promise uses (the element the build bar
 * shows, snippets/dispatch-promise.liquid): the cutoff, Friday's own cutoff
 * for Saturday-morning dispatch, the shop's non-dispatch dates and the
 * warehouse's timezone. Read from the element the section renders into
 * <template data-byo-v4-dispatch>; the theme's defaults without it.
 */
export function dispatchRules() {
  const t = document.querySelector('template[data-byo-v4-dispatch]');
  const el = t && t.content ? t.content.querySelector('[data-cutoff]') : null;
  const d = (el && el.dataset) || {};
  const clock = (v, h, m) => {
    const [a, b] = String(v || '').split(':').map((x) => parseInt(x, 10));
    return (Number.isFinite(a) ? a : h) * 3600 + (Number.isFinite(b) ? b : m) * 60;
  };
  return {
    tz: d.timezone || 'Europe/London',
    cutoff: clock(d.cutoff, 12, 0),
    /* The Friday cutoff names a minute, so it runs to the end of it. */
    friday: clock(d.fridayCutoff, 23, 59) + 59,
    closed: String(d.nonDispatchDates || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
  };
}

/** Now, as the warehouse's clock reads it. */
function zoneNow(tz) {
  try {
    const out = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .formatToParts(new Date())
      .forEach((p) => {
        if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
      });
    if (out.hour === 24) out.hour = 0;
    if (out.year) return out;
  } catch (_) {
    /* fall through to the phone's own clock */
  }
  const n = new Date();
  return { year: n.getFullYear(), month: n.getMonth() + 1, day: n.getDate(), hour: n.getHours(), minute: n.getMinutes(), second: n.getSeconds() };
}

/**
 * The day a box ordered now is dispatched, by the same four states as
 * assets/dispatch-promise.js: today before the cutoff on a working day;
 * Saturday for a Friday afternoon order; otherwise the next dispatch day.
 */
export function dispatchDay(rules, now) {
  const today = dayNum(now.year, now.month, now.day);
  const open = (n) => {
    const wd = weekdayOf(n);
    return wd !== 0 && wd !== 6 && !rules.closed.includes(isoOf(n));
  };
  const secs = now.hour * 3600 + now.minute * 60 + now.second;
  if (open(today) && secs < rules.cutoff) return today;
  if (open(today) && weekdayOf(today) === 5 && secs <= rules.friday) return today + 1;
  for (let k = 1; k <= 14; k += 1) if (open(today + k)) return today + k;
  return today + 1;
}

/**
 * DatePicker — replaces `<input type="date">` (D70). Alan, 26 Sep 2026: make
 * it impossible to choose the day of the order, say plainly to pick a later
 * one, and colour the days by the delivery they need. iPhone Safari's own
 * picker let a customer choose today whatever `min` said, and could never
 * colour a day.
 *
 * Counted from the day the box would be dispatched (`dispatchDay`): a day
 * sooner than `dateExpressDays` after it cannot be reached and is greyed out,
 * today always among them; one sooner than `dateLeadDays` after it needs
 * Express; anything later, any delivery. Ordered on a weekday morning, that
 * is Alan's rule exactly: tomorrow needs Express, the day after is fine.
 */
export function DatePicker({ value, onPick, config }) {
  const rules = useMemo(dispatchRules, []);
  const now = useMemo(() => zoneNow(rules.tz), [rules]);
  const today = dayNum(now.year, now.month, now.day);
  const ship = dispatchDay(rules, now);

  const num = (v, f) => (typeof v === 'number' && Number.isFinite(v) ? v : f);
  const expressDays = Math.max(1, num(config.dateExpressDays, 1));
  const anyDays = Math.max(expressDays, num(config.dateLeadDays, 2));
  const first = Math.max(today + 1, ship + expressDays);
  const easy = Math.max(first, ship + anyDays);
  const kind = (n) => (n < first ? 'off' : n < easy ? 'express' : 'any');

  const chosen = dayOfIso(value);
  const lowest = monthOfDay(first);
  const highest = addMonths(lowest, 11);
  const [view, setView] = useState(() => monthOfDay(chosen != null && chosen >= first ? chosen : first));

  const start = dayNum(view.y, view.m, 1);
  const end = dayNum(addMonths(view, 1).y, addMonths(view, 1).m, 1);
  const lead = (weekdayOf(start) + 6) % 7;
  const cells = [...Array(lead).fill(null), ...Array.from({ length: end - start }, (_, i) => start + i)];
  const title = new Date(start * DAY_MS).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const long = (n) => new Date(n * DAY_MS).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

  return html`
    <div class="byo-v4-cal">
      <p class="byo-v4-cal__say">Choose a day after today for them to open it.</p>

      <div class="byo-v4-cal__head">
        <button
          type="button"
          class="byo-v4-cal__nav"
          aria-label="Previous month"
          disabled=${monthKey(view) <= monthKey(lowest)}
          onClick=${() => setView(addMonths(view, -1))}
        >
          <${Icon} name="chevL" size=${18} stroke=${2.5} />
        </button>
        <div class="byo-v4-cal__month" aria-live="polite">${title}</div>
        <button
          type="button"
          class="byo-v4-cal__nav"
          aria-label="Next month"
          disabled=${monthKey(view) >= monthKey(highest)}
          onClick=${() => setView(addMonths(view, 1))}
        >
          <${Icon} name="chevR" size=${18} stroke=${2.5} />
        </button>
      </div>

      <div class="byo-v4-cal__grid" role="group" aria-label=${title}>
        ${['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(
          (d, i) => html`<span key=${`h${i}`} class="byo-v4-cal__dow" aria-hidden="true">${d}</span>`
        )}
        ${cells.map((n, i) => {
          if (n == null) return html`<span key=${`p${i}`} class="byo-v4-cal__pad"></span>`;
          const k = kind(n);
          const on = chosen === n;
          return html`
            <button
              key=${n}
              type="button"
              class=${`byo-v4-cal__day is-${k}${on ? ' is-on' : ''}${n === today ? ' is-today' : ''}`}
              disabled=${k === 'off'}
              aria-pressed=${on}
              aria-label=${`${long(n)}${n === today ? ', today' : ''}${k === 'express' ? ', needs Express' : ''}${k === 'off' ? ', too soon' : ''}`}
              onClick=${() => onPick(isoOf(n))}
            >
              ${new Date(n * DAY_MS).getUTCDate()}
            </button>
          `;
        })}
      </div>

      <div class="byo-v4-cal__key">
        <span class="byo-v4-cal__keyitem"><i class="byo-v4-cal__swatch is-express" aria-hidden="true"></i>Needs Express</span>
        <span class="byo-v4-cal__keyitem"><i class="byo-v4-cal__swatch is-any" aria-hidden="true"></i>Any delivery</span>
      </div>

      ${chosen != null &&
      (kind(chosen) === 'off'
        ? /* A date carried in on a box opened from the cart can have passed. */
          html`<${Err}>${prettyDate(value)} is too soon now. Pick a later day.<//>`
        : html`<div class="byo-v4-date__done">
            <${Pill} tone="lime">Open on ${prettyDate(value)}<//>
            <span class="byo-v4-date__say">
              ${kind(chosen) === 'express'
                ? 'Choose Express at checkout so it arrives in time.'
                : 'We time dispatch so it lands just before.'}
            </span>
          </div>`)}
    </div>
  `;
}

/**
 * PersonalTouches — byo-stages-v3.jsx:273. DESIGN-SPEC.md §4.2.
 */
export function PersonalTouches({ b, config, showErrors }) {
  const box = b.box;
  const limits = config.limits || { song: 250, message: 300, from: 40 };
  const extras = config.extras || {};

  /* D70: the song is off until it can offer a library of songs to pick from.
     Everything behind it stays, dormant — its price, its cart line, its place
     in the payload — and the row comes back with the "Offer the song QR code"
     setting. A box that already carries a song (an older box opened from the
     cart) still shows the row, so the song can be seen and switched off. */
  const songRow = config.songEnabled === true || box.song.on;
  const dateRow = config.dateEnabled !== false || box.date.on;

  /* The upload in flight, or null: `{ name, preview, big, phase, pct }`. It
     lives here rather than in the row body so switching the row off and on
     mid-upload loses nothing — the bar comes back where it had got to. D55. */
  const [upload, setUpload] = useState(null);
  const [uploadErr, setUploadErr] = useState(null);
  const fileInput = useRef(null);

  const price = (k, fallback) => {
    const p = extras[k] && typeof extras[k].price === 'number' ? extras[k].price : fallback;
    return b.money(p);
  };

  const pickPhoto = async (file) => {
    if (!file) return;
    setUploadErr(null);
    /* A thumbnail made on the phone, so the customer sees which photo is
       going before a single byte has left. Revoked when the upload ends. */
    const preview = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
    setUpload({ name: file.name, preview, big: file.size > BIG_PHOTO, phase: 'sending', pct: 0 });
    try {
      /* Progress events arrive many times a second; only a new percentage or
         a new phase is worth a render. */
      const url = await b.uploadPhoto(file, ({ phase, pct }) =>
        setUpload((u) => (!u || (u.phase === phase && u.pct === pct) ? u : { ...u, phase, pct }))
      );
      b.setPhoto({ file: { name: file.name, url } });
    } catch (e) {
      /* Never record a photo we cannot produce a URL for — GIFTSHIP-API.md §4. */
      setUploadErr("That didn't upload. Try again, or switch Photo off.");
    } finally {
      setUpload(null);
      if (preview) URL.revokeObjectURL(preview);
    }
  };

  /* The row keeps its live region mounted whether or not anything is
     uploading: one that is added already holding its text is often not read
     out at all, and "Uploading your photo…" is the line a screen reader user
     most needs to hear. It carries the phase, never the percentage. */
  const uploadSay = upload ? uploadLine(upload) : '';

  /* `store.toggle(key)` flips a FLAT boolean, so it suits `messageOn` and
     nothing else here: photo, song and date are `{ on, ... }` objects, and
     toggling them that way would replace the object with `false` and take the
     customer's text with it. Each nested row goes through its own merging
     setter instead. */
  const setRowOn = {
    photo: (on) => b.setPhoto({ on }),
    song: (on) => b.setSong({ on }),
    date: (on) => b.setDate({ on }),
    message: (on) => {
      if (!!box.messageOn !== !!on) b.toggle('messageOn');
    },
  };

  /* A row switched on lower down the card opens its fields under the build
     bar. Bring them into view once they have rendered. `block: 'nearest'`
     scrolls the stage's scroller, not the page, and only as far as it needs. */
  const reveal = (key) => {
    requestAnimationFrame(() => {
      const body = document.querySelector(`[data-touch="${key}"] .byo-v4-touch__body`);
      if (!body) return;
      const reduced =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      body.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    });
  };

  const toggle = (key) => (on) => {
    setRowOn[key](on);
    if (on) reveal(key);
  };

  return html`
    <div class="byo-v4-touches">
      <div class="byo-v4-card byo-v4-touches__card">
        <${TouchRow}
          id="message"
          icon="edit"
          title="Gift Message"
          sub="Free · handwritten gift card"
          on=${box.messageOn}
          err=${showErrors && !b.messageOk}
          onToggle=${toggle('message')}
        >
          <textarea
            class=${`byo-v4-field byo-v4-field--area${showErrors && !b.messageOk ? ' is-err' : ''}`}
            rows="4"
            placeholder="Write something they’ll keep"
            maxlength=${limits.message}
            value=${box.message}
            onInput=${(e) => b.setMessage(e.target.value)}
          ></textarea>
          <div class="byo-v4-fieldfoot">
            <span></span>
            <span>${box.message.length}/${limits.message}</span>
          </div>
          ${/* D70: no "From" field. Who it is from goes in the message itself,
               and this is the reminder to put it there. */ ''}
          <div class="byo-v4-unsigned">Don’t forget to say who it’s from.</div>
          ${showErrors && !b.messageOk && html`<${Err}>Write your message, or switch Gift Message off<//>`}
        <//>

        <${TouchRow}
          id="photo"
          icon="camera"
          title="Printed Keepsake Photo"
          sub=${`+${price('photo', 250)} · printed and tucked inside`}
          on=${box.photo.on}
          err=${showErrors && !b.photoOk && !upload}
          onToggle=${toggle('photo')}
        >
          <input
            ref=${fileInput}
            class="byo-v4-file"
            type="file"
            accept="image/jpeg,image/png"
            onChange=${(e) => {
              /* Cleared once the attempt ends: an input only fires `change`
                 when its value changes, so after a failure, choosing the same
                 photo again — what "Try again" asks for — did nothing. */
              const input = e.target;
              pickPhoto(input.files && input.files[0]).finally(() => {
                input.value = '';
              });
            }}
          />
          ${upload
            ? html`<${UploadStatus} upload=${upload} say=${uploadSay} />`
            : box.photo.file
            ? html`<div class="byo-v4-photo">
                <img class="byo-v4-photo__thumb" src=${box.photo.file.url} alt="" />
                <div class="byo-v4-photo__meta">
                  <div class="byo-v4-photo__name">${box.photo.file.name}</div>
                  <${Pill} tone="lime">Uploaded<//>
                </div>
                <button type="button" class="byo-v4-photo__replace" onClick=${() => fileInput.current.click()}>
                  Replace
                </button>
              </div>`
            : html`<button
                type="button"
                class=${`byo-v4-drop${showErrors && !b.photoOk ? ' is-err' : ''}`}
                onClick=${() => fileInput.current.click()}
              >
                <span class="byo-v4-drop__t">Upload A Photo</span>
                <span class="byo-v4-drop__s">JPG or PNG</span>
              </button>`}
          <p class="byo-v4-upload__live" aria-live="polite">
            ${upload ? (upload.phase === 'finishing' ? uploadLine(upload) : 'Uploading your photo…') : ''}
          </p>
          ${uploadErr && html`<${Err}>${uploadErr}<//>`}
          ${showErrors && !b.photoOk && !uploadErr && !upload && html`<${Err}>Add your photo to continue, or switch Photo off<//>`}
        <//>

        ${songRow &&
        html`<${TouchRow}
          id="song"
          icon="music"
          title="Song QR Code"
          sub=${`+${price('song', 250)} · a QR code that plays their song`}
          on=${box.song.on}
          err=${showErrors && !b.songOk}
          onToggle=${toggle('song')}
        >
          <input
            class=${`byo-v4-field${showErrors && !b.songOk ? ' is-err' : ''}`}
            type="text"
            placeholder="Artist and song title"
            maxlength=${limits.song}
            value=${box.song.text}
            onInput=${(e) => b.setSong({ text: e.target.value })}
          />
          <div class="byo-v4-fieldfoot">
            <span>We’ll find it on Spotify for you</span>
            <span>${box.song.text.length}/${limits.song}</span>
          </div>
          ${showErrors && !b.songOk && html`<${Err}>Tell us the artist and song<//>`}
        <//>`}

        ${dateRow &&
        html`<${TouchRow}
          id="date"
          icon="calendar"
          title="Save-The-Date Sticker"
          sub="Free · a sticker saying when to open"
          on=${box.date.on}
          err=${showErrors && !b.dateOk}
          onToggle=${toggle('date')}
        >
          <${DatePicker} value=${box.date.value} onPick=${(value) => b.setDate({ value })} config=${config} />
          ${showErrors && !b.dateOk && html`<${Err}>Pick the day it should be opened<//>`}
        <//>`}
      </div>
    </div>
  `;
}

/**
 * PersonaliseStage — §4, D70. The lime callout, then the touches. How the
 * box is given, and the keepsake box with it, is chosen on Wrap It Up.
 */
export function PersonaliseStage({ b, config, showErrors }) {
  return html`
    <div class="byo-v4-p byo-v4-fill__inner">
      <div class="byo-v4-callout byo-v4-callout--block byo-v4-p__callout">
        <${Icon} name="gift" size=${18} />
        <span><strong>Make it theirs.</strong> Add a gift message, a printed photo or a save-the-date sticker. All optional.</span>
      </div>
      <div class="byo-v4-p__col">
        <${PersonalTouches} b=${b} config=${config} showErrors=${showErrors} />
      </div>
    </div>
  `;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { DatePicker, Err, PersonalTouches, PersonaliseStage, Toggle, TouchRow, UploadStatus, WrapChooser, dispatchDay, dispatchRules, minDate, prettyDate, uploadLine });
